import {baseApi} from './base'
import type {AvailabilityPieInterface} from '@interfaces/availabilityPie.ts'
import type {BaseFetchResultsInterface} from '@interfaces/base'
import type {CalendarMetricInterface} from '@interfaces/calendarMetric.ts'
import type {ProductionTimelineByEquipmentInterface} from '@interfaces/productionTimeline.ts'

interface AvailabilityData {
  date: string
  classification: string
  duration: number
  id: string
}

const lineDashboardAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAvailabilityPie: builder.query<
      AvailabilityPieInterface[],
      {lineId: string; startDate: string; endDate: string}
    >({
      query: ({lineId, startDate, endDate}) =>
        `machinesubsystems/availability_pies/?machine=${lineId}&t_start__gte=${startDate}&t_end__lte=${endDate}`,
      transformResponse: (response: BaseFetchResultsInterface<AvailabilityPieInterface>) =>
        response.results,
    }),
    getAvailabilityPiePerEquipment: builder.query<
      AvailabilityPieInterface,
      {equipmentId: string; startDate?: string; endDate?: string}
    >({
      query: ({equipmentId, startDate, endDate}) =>
        `machinesubsystems/${equipmentId}/availability_pie/?t_start__gte=${startDate}&t_end__lte=${endDate}`,
      extraOptions: {refetchOnMountOrArgChange: true},
    }),
    getAvailabilityPerDay: builder.query<
      AvailabilityData[],
      {
        equipmentId: string
        startDate: string
        endDate: string
        summarize_by: string
      }
    >({
      // No timeZoneOffset: core resolves the viewer's zone off the auth session,
      // the same as the batch and comment timelines. Sending the browser's
      // offset bucketed these against the machine clock instead (AB#5973).
      query: ({equipmentId, startDate, endDate, summarize_by}) =>
        `machinesubsystems/${equipmentId}/daily_availabilities/?t_start__gte=${startDate}&t_end__lte=${endDate}&summarize_by=${summarize_by}`,
    }),
    getCalendarMetricPerDay: builder.query<
      CalendarMetricInterface[],
      {lineId: string; equipmentId: string; metric: string}
    >({
      query: ({lineId, equipmentId, metric}) =>
        `machinesubsystems/${equipmentId}/metric_per_day/?machine=${lineId}&metric=${metric}`,
      transformResponse: (response: BaseFetchResultsInterface<CalendarMetricInterface>) =>
        response.results,
    }),
    getEquipmentsTimeline: builder.query<
      ProductionTimelineByEquipmentInterface,
      {
        lineId?: string
        equipmentIds?: string[]
        startDate: string
        endDate: string
        anomalyScoreFilter?: number
      }
    >({
      query: ({lineId, equipmentIds, startDate, endDate, anomalyScoreFilter = 0}) => ({
        url: 'batches/timeline_by_mss/',
        params: {
          machine: equipmentIds?.length ? undefined : lineId,
          machine_sub_systems: equipmentIds?.length ? equipmentIds.join(',') : undefined,
          start_date: startDate,
          end_date: endDate,
          anomaly_score__gte: anomalyScoreFilter,
        },
      }),
      extraOptions: {refetchOnMountOrArgChange: true},
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetAvailabilityPieQuery,
  useGetAvailabilityPiePerEquipmentQuery,
  useLazyGetAvailabilityPiePerEquipmentQuery,
  useGetCalendarMetricPerDayQuery,
  useGetAvailabilityPerDayQuery,
  useGetEquipmentsTimelineQuery,
} = lineDashboardAPI
